/**
 * ReportCentreKit.tsx — small shared presentation pieces for the Reporting Centre
 * pages (recon + audit + rule families). Complements AnalyticsKit; anything already
 * in AnalyticsKit is NOT duplicated here.
 */
import type { ReactNode } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { cn } from '@/utils/cn';

// ── Tone-based pill ───────────────────────────────────────────────────────────

export type PillTone = 'green' | 'red' | 'amber' | 'grey' | 'blue' | 'purple';

export const PILL_TONE_CLS: Record<PillTone, string> = {
  green:  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  red:    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  amber:  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  grey:   'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
  blue:   'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
};

export const Pill = ({ label, tone, title }: { label: string; tone: PillTone; title?: string }) => (
  <span
    className={cn(
      'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
      PILL_TONE_CLS[tone],
    )}
    title={title}
  >
    {label}
  </span>
);

/** Severity → tone used by the rule-driven reports. */
export function severityTone(severity: string): PillTone {
  switch (severity.toUpperCase()) {
    case 'HIGH':   return 'red';
    case 'MEDIUM': return 'amber';
    default:       return 'grey';
  }
}

// ── Count chip (summary "N per bucket" chips above tables) ────────────────────

export const CountChip = ({ label, count, tone = 'grey' }: {
  label: string;
  count: number;
  tone?: PillTone;
}) => (
  <span
    className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap',
      PILL_TONE_CLS[tone],
    )}
  >
    {label}
    <span className="font-bold tabular-nums">{count.toLocaleString('en-US')}</span>
  </span>
);

// ── Informative banners ───────────────────────────────────────────────────────

/** Grey banner for NOT_CONFIGURED states — informative, never an error. */
export const InfoBanner = ({ children }: { children: ReactNode }) => (
  <div className="flex items-start gap-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-300">
    <Info size={16} className="text-gray-400 shrink-0 mt-0.5" />
    <div className="min-w-0">{children}</div>
  </div>
);

/** Amber banner for warnings such as a truncated scan window. */
export const WarnBanner = ({ children }: { children: ReactNode }) => (
  <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
    <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
    <div className="min-w-0">{children}</div>
  </div>
);

// ── Tabs ──────────────────────────────────────────────────────────────────────

export const TabBar = ({ tabs, active, onChange }: {
  tabs: { key: string; label: string }[];
  active: string;
  onChange: (key: string) => void;
}) => (
  <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
    {tabs.map(t => (
      <button
        key={t.key}
        onClick={() => onChange(t.key)}
        className={cn(
          'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
          active === t.key
            ? 'border-brand-500 text-brand-600 dark:text-brand-400'
            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
        )}
      >
        {t.label}
      </button>
    ))}
  </div>
);

// ── Pager ─────────────────────────────────────────────────────────────────────

interface PagerProps {
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  shownCount: number;
  loading: boolean;
  onPage: (pageNumber: number) => void;
  noun?: string;
}

export const Pager = ({
  pageNumber, totalPages, totalCount, shownCount, loading, onPage, noun = 'rows',
}: PagerProps) => (
  <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
    <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
      Showing {shownCount.toLocaleString('en-US')} of {totalCount.toLocaleString('en-US')} {noun}
      {' · '}Page {pageNumber} of {Math.max(totalPages, 1)}
    </span>
    <div className="flex items-center gap-1">
      <button
        onClick={() => onPage(pageNumber - 1)}
        disabled={loading || pageNumber <= 1}
        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        title="Previous page"
      >
        <ChevronLeft size={14} />
        Prev
      </button>
      <button
        onClick={() => onPage(pageNumber + 1)}
        disabled={loading || pageNumber >= totalPages}
        className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        title="Next page"
      >
        Next
        <ChevronRight size={14} />
      </button>
    </div>
  </div>
);

// ── Misc ──────────────────────────────────────────────────────────────────────

/** Em-dash placeholder for null text cells — never render a fake value. */
export const Dash = () => <span className="text-gray-400 dark:text-gray-600">—</span>;

export const DEFAULT_PAGE_SIZE = 100;

export function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Failed to load report';
}
